export interface ChipColors {
  label: string;
  fg: string;
  bg: string;
}

/**
 * Product department + data-flag chip palettes for the Product-editor screens
 * (Figma "Product editor list"). Department chips are a fixed light-tint palette;
 * unknown departments fall back to slate. Flag chips summarize a product's data
 * state (recall / SNAP / missing image / non-food / variant-duplicate).
 */

const DEPARTMENT_CHIPS: Record<string, ChipColors> = {
  pantry: { label: 'Pantry', bg: '#FEF9C3', fg: '#854D0E' },
  beverages: { label: 'Beverages', bg: '#DBEAFE', fg: '#1D4ED8' },
  dairy: { label: 'Dairy', bg: '#FEF3C7', fg: '#A16207' },
  household: { label: 'Household', bg: '#E0E7FF', fg: '#4338CA' },
  produce: { label: 'Produce', bg: '#DCFCE7', fg: '#15803D' },
  'meat & seafood': { label: 'Meat & seafood', bg: '#FEE2E2', fg: '#B91C1C' },
  meat: { label: 'Meat', bg: '#FEE2E2', fg: '#B91C1C' },
  seafood: { label: 'Seafood', bg: '#FEE2E2', fg: '#B91C1C' },
  pharmacy: { label: 'Pharmacy', bg: '#ECFCCB', fg: '#4D7C0F' },
  'frozen food': { label: 'Frozen', bg: '#CFFAFE', fg: '#0E7490' },
  frozen: { label: 'Frozen', bg: '#CFFAFE', fg: '#0E7490' },
  bakery: { label: 'Bakery', bg: '#FCE7D6', fg: '#9A3412' },
  deli: { label: 'Deli', bg: '#FFEDD5', fg: '#C2410C' },
  baby: { label: 'Baby', bg: '#FCE7F3', fg: '#BE185D' },
  pet: { label: 'Pet', bg: '#F3E8FF', fg: '#7E22CE' },
  'beauty and personal care': { label: 'Beauty & care', bg: '#FCE7F3', fg: '#BE185D' },
  'health and nutrition': { label: 'Health', bg: '#ECFCCB', fg: '#4D7C0F' },
};

export function departmentChip(department: string | null | undefined): ChipColors {
  const key = (department ?? '').trim().toLowerCase();
  return DEPARTMENT_CHIPS[key] ?? { label: department || 'Unclassified', bg: '#E2E8F0', fg: '#475569' };
}

export type ProductFlag = 'recall' | 'snap' | 'noimage' | 'nonfood' | 'dup' | 'ok';

const FLAG_CHIPS: Record<ProductFlag, ChipColors> = {
  recall: { label: 'Recall', bg: '#FEE2E2', fg: '#B91C1C' },
  snap: { label: 'SNAP', bg: '#DCFCE7', fg: '#15803D' },
  noimage: { label: 'No image', bg: '#FEF3C7', fg: '#B45309' },
  nonfood: { label: 'Non-food', bg: '#E2E8F0', fg: '#475569' },
  dup: { label: 'Dup?', bg: '#FEF3C7', fg: '#B45309' },
  ok: { label: 'OK', bg: '#DCFCE7', fg: '#15803D' },
};

export function flagChip(flag: ProductFlag): ChipColors {
  return FLAG_CHIPS[flag];
}

/** The single most salient flag for a product row (priority-ordered). */
export function primaryFlag(p: {
  hasActiveRecall?: boolean;
  isEdible?: boolean;
  hasAllImages?: boolean;
  variantGroupId?: string | null;
  isSnapEligible?: boolean;
}): ProductFlag {
  if (p.hasActiveRecall) return 'recall';
  if (p.isEdible === false) return 'nonfood';
  if (p.hasAllImages === false) return 'noimage';
  if (p.isSnapEligible) return 'snap';
  return 'ok';
}
